import React, { useContext, useEffect } from 'react';
import { Button, Input, Label, Flex, Flexed } from '../../Shared';
import { FiMinusCircle } from 'react-icons/fi';
import { Tab, TabList, TabPanel, Tabs } from 'react-tabs';
import { v4 as uuidv4 } from 'uuid';
import Bookitem from './Bookitem';
import BookEstimate from '../../Context/BookEstimate';
import Collapse from '../../Util/Collapse';
import { toast } from 'react-toastify';
import { paperthickness,  wireo, plastikol } from './Calcs';

const Books = ({ closeBook, setEstitems, estitems, editingItem = null }) => {
  const {
    items,
    setItems,
    checkquans,
    quanbox,
    bookinfo,
    setBookinfo,
    coilinfo,
    setCoilinfo,
    binding,
    setBinding,
    getlabor,
    gettotlabor,
    getmat,
    gettotmat,
    changecoilinfo,
  } = useContext(BookEstimate);

    // SAFE LOAD EDITING BOOK — PREVENTS ALL UNDEFINED ERRORS
  useEffect(() => {
    if (editingItem) {
      // Safely populate bookinfo with defaults if fields are missing
      setBookinfo({
        bookname: editingItem.bookname || `Book ${estitems.books?.length + 1 || 1}`,
        width: editingItem.width ?? 8.5,
        length: editingItem.length ?? 11,
        spine: editingItem.spine ?? 0,
        textpages: editingItem.textpages ?? 0,
        textthickness: editingItem.textthickness ?? '',
        textname: editingItem.textname || '',
        coverpages: editingItem.coverpages ?? 0,
        coverthickness: editingItem.coverthickness ?? '',
        covername: editingItem.covername || '',
        otherpages: editingItem.otherpages ?? 0,
        otherthickness: editingItem.otherthickness ?? '',
        othername: editingItem.othername || '',
        holes: editingItem.holes ?? 0,
        tabs: editingItem.tabs ?? 0,
        slipsheet: editingItem.slipsheet ?? false,
        collatepu: editingItem.collatepu || '',
        insert: editingItem.insert || '',
        cuts: editingItem.cuts || '',
        punchmr: editingItem.punchmr ?? 0,
        coilmr: editingItem.coilmr ?? 0,
        totalpages: editingItem.totalpages ?? 0,
        thickness: editingItem.thickness ?? 0,
        additionalthickness: editingItem.additionalthickness ?? 0,
      });

      // Safe binding
      const bindingType = editingItem.binding || '';
      setBinding({
        type: bindingType,
        thickness: bindingType === 'Wire-O' ? 0.125 : bindingType === 'Plastikol' ? 0.0787 : 0,
      });

      // Safe coilinfo
      setCoilinfo(editingItem.coilinfo || { pitch: '', decimal: '', color: 'White' });

      // Safe quantities — always valid objects
      const savedQuans = editingItem.quantities || [];
      setItems(
        savedQuans.length > 0
          ? savedQuans.map((q) => ({
              id: uuidv4(),
              quantity: q.quantity || 100,
              laborpercent: 100,
              materialpercent: 100,
            }))
          : []
      );
    } else {
      // Fresh book — clean state
      setBookinfo({
        width: 8.5,
        length: 11,
        spine: 0,
        bookname: '',
        textpages: 0,
        coverpages: 0,
        otherpages: 0,
        holes: 0,
        tabs: 0,
        slipsheet: false,
        punchmr: 0,
        coilmr: 0,
      });
      setBinding({ type: '' });
      setCoilinfo({});
      setItems([]);
    }
  }, [editingItem, estitems.books]);

  const removequantity = (id) => {
    setItems((prev) => prev.filter((item) => item.id !== id));
  };

  const handleSaveBook = () => {
    if (items.length === 0) {
      toast.error('Please add at least one quantity');
      return;
    }

    const bookData = {
      id: editingItem?.id || uuidv4(),
      type: 'book',
      bookname: bookinfo?.bookname || `Book ${estitems.books?.length + 1 || 1}`,
      width: parseFloat(bookinfo?.width) || 8.5,
      length: parseFloat(bookinfo?.length) || 11,
      spine: parseFloat(bookinfo?.spine) || 0,
      textpages: parseInt(bookinfo?.textpages) || 0,
      textthickness: bookinfo?.textthickness || 0,
      textname: bookinfo?.textname || '',
      coverpages: parseInt(bookinfo?.coverpages) || 0,
      coverthickness: bookinfo?.coverthickness || 0,
      covername: bookinfo?.covername || '',
      otherpages: parseInt(bookinfo?.otherpages) || 0,
      otherthickness: bookinfo?.otherthickness || 0,
      othername: bookinfo?.othername || '',
      binding: binding?.type || 'None',
      coilinfo: coilinfo || {},
      holes: parseInt(bookinfo?.holes) || 0,
      tabs: parseInt(bookinfo?.tabs) || 0,
      slipsheet: !!bookinfo?.slipsheet,
      collatepu: bookinfo?.collatepu || '',
      insert: bookinfo?.insert || '',
      cuts: bookinfo?.cuts || '',
      punchmr: parseFloat(bookinfo?.punchmr) || 0,
      coilmr: parseFloat(bookinfo?.coilmr) || 0,
      totalpages: parseInt(bookinfo?.totalpages) || 0,
      thickness: parseFloat(bookinfo?.thickness) || 0,
      additionalthickness: parseFloat(bookinfo?.additionalthickness) || 0,
      quantities: items.map((item) => {
        const laborCost = getlabor(item);
        const matCost = getmat(item);
        const laborTotal = gettotlabor(item, laborCost);
        const matTotal = gettotmat(item, matCost);
        const lineTotal = laborTotal + matTotal;

        return {
          quantity: item.quantity,
          pricePerUnit: parseFloat((lineTotal / item.quantity).toFixed(4)),
          totalPrice: parseFloat(lineTotal.toFixed(2)),
        };
      }),
          grandTotal:200.00,
      // grandTotal: items
      //   .reduce((sum, item) => {
      //     const l = gettotlabor(item, getlabor(item));
      //     const m = gettotmat(item, getmat(item));
      //     return sum + l + m;
      //   }, 0)
      //   .toFixed(2),
    };

    setEstitems((prev) => ({
      ...prev,
      books: editingItem
        ? prev.books.map((b) => (b.id === editingItem.id ? bookData : b))
        : [...(prev.books || []), bookData],
    }));

    toast.success(editingItem ? 'Book updated!' : 'Book added to estimate!');
    closeBook();
  };

  return (
    <div
      className="fixed inset-0 flex items-center justify-center z-50"
      style={{ background: 'rgba(0,0,0,0.6)' }}
    >
      <div
        className="bg-white rounded-lg max-w-[95vw] w-full max-h-[90vh] overflow-y-auto"
        style={{ padding: '24px', boxShadow: '0 20px 40px rgba(0,0,0,0.3)' }}
      >
        <h1
          className="text-3xl font-bold mb-8 text-center"
          style={{ color: '#1976d2' }}
        >
          {editingItem ? 'Edit Book' : 'Add Book'}
        </h1>

        {/* === DIMENSIONS === */}
        <div className="mb-8 p-5 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl border border-blue-200">

                  {/* === ADD QUANTITY === */}
        <div className="mb-10 p-14 bg-green-50 rounded-xl " style={{padding:'15px 3px'}}>
          <Label className="font-bold text-green-800">Add Quantity (Press Enter)</Label>
          <Input
            ref={quanbox}
            type="number"
            placeholder="e.g. 1000"
            onKeyDown={checkquans}
            className="mt-2 text-lg"
            style={{ width: '220px' }}
          />
        </div>


          <h3 className="font-bold text-lg mb-4 text-blue-800">Dimensions</h3>
          <Flexed gap="20px">
            <div>
              <Label>Flat Width (Length)</Label>
              <Input
                type="number"
                step="0.1"
                value={bookinfo?.length || ''}
                onChange={(e) => setBookinfo((p) => ({ ...p, length: e.target.value }))}
                placeholder="11"
              />
            </div>
            <div>
              <Label>Height (Width)</Label>
              <Input
                type="number"
                step="0.1"
                value={bookinfo?.width || ''}
                onChange={(e) => setBookinfo((p) => ({ ...p, width: e.target.value }))}
                placeholder="8.5"
              />
            </div>
            <div>
              <Label>Spine Thickness</Label>
            
              <Input
                type="number"
                step="0.01"
                value={bookinfo?.spine || ''}
                onChange={(e) => setBookinfo((p) => ({ ...p, spine: e.target.value }))}
              />
            </div>
          </Flexed>
        </div>

        {/* === TEXT / COVERS / OTHER === */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          {/* Text */}
          <div className="p-5 bg-blue-50 rounded-xl border border-blue-200">
            <h3 className="font-bold text-blue-700 mb-3">Text Pages</h3>
            <Flexed>
              <Flex flex="1">
                <Label>Pages</Label>
                <Input
                  value={bookinfo?.textpages || ''}
                  onChange={(e) => setBookinfo((p) => ({ ...p, textpages: e.target.value }))}
                />
              </Flex>
              <Flex flex="2">
                <Label>Stock</Label>
                <select
                  className="w-full px-3 py-2 border rounded-lg"
                  value={bookinfo?.textthickness || ''}
                  onChange={(e) =>
                    setBookinfo((p) => ({
                      ...p,
                      textthickness: e.target.value,
                      textname: e.target.options[e.target.selectedIndex].text,
                    }))
                  }
                >
                  <option value="">Select Paper</option>
                  {paperthickness?.map((p) => (
                    <option key={p.thickness} value={p.thickness}>
                      {p.name} - {p.thickness}"
                    </option>
                  ))}
                </select>
              </Flex>
            </Flexed>
          </div>

          {/* Covers */}
          <div className="p-5 bg-orange-50 rounded-xl border border-orange-200">
            <h3 className="font-bold text-orange-700 mb-3">Covers</h3>
            <Flexed>
              <Flex flex="1">
                <Label>Pages</Label>
                <Input
                  value={bookinfo?.coverpages || ''}
                  onChange={(e) => setBookinfo((p) => ({ ...p, coverpages: e.target.value }))}
                />
              </Flex>
              <Flex flex="2">
                <Label>Stock</Label>
                <select
                  className="w-full px-3 py-2 border rounded-lg"
                  value={bookinfo?.coverthickness || ''}
                  onChange={(e) =>
                    setBookinfo((p) => ({
                      ...p,
                      coverthickness: e.target.value,
                      covername: e.target.options[e.target.selectedIndex].text,
                    }))
                  }
                >
                  <option value="">Select Paper</option>
                  {paperthickness?.map((p) => (
                    <option key={p.thickness} value={p.thickness}>
                      {p.name} - {p.thickness}"
                    </option>
                  ))}
                </select>
              </Flex>
            </Flexed>
          </div>

          {/* Other/Inserts */}
          <div className="p-5 bg-purple-50 rounded-xl border border-purple-200">
            <h3 className="font-bold text-purple-700 mb-3">Other / Inserts</h3>
            <Flexed>
              <Flex flex="1">
                <Label>Pages</Label>
                <Input
                  value={bookinfo?.otherpages || ''}
                  onChange={(e) => setBookinfo((p) => ({ ...p, otherpages: e.target.value }))}
                />
              </Flex>
              <Flex flex="2">
                <Label>Stock</Label>
                <select
                  className="w-full px-3 py-2 border rounded-lg"
                  value={bookinfo?.otherthickness || ''}
                  onChange={(e) =>
                    setBookinfo((p) => ({
                      ...p,
                      otherthickness: e.target.value,
                      othername: e.target.options[e.target.selectedIndex].text,
                    }))
                  }
                >
                  <option value="">Select Paper</option>
                  {paperthickness?.map((p) => (
                    <option key={p.thickness} value={p.thickness}>
                      {p.name} - {p.thickness}"
                    </option>
                  ))}
                </select>
              </Flex>
            </Flexed>
          </div>
        </div>

        {/* === BINDING === */}
        <div className="mb-8 p-5 bg-amber-50 rounded-xl border border-amber-300">
          <Label className="font-bold text-amber-800">Binding Type</Label>
          <select
            className="ml-4 px-4 py-2 border-2 border-amber-400 rounded-lg font-bold"
            value={binding?.type || ''}
            onChange={(e) =>
              setBinding({
                type: e.target.value,
                thickness: e.target.value === 'Wire-O' ? 0.125 : e.target.value === 'Plastikol' ? 0.0787 : 0,
              })
            }
          >
            <option value="">None</option>
            <option value="Wire-O">Wire-O</option>
            <option value="Plastikol">Plastikol</option>
          </select>

          {binding?.type && (
            <div className="mt-4 grid grid-cols-2 md:grid-cols-4 gap-4">
              <div>
                <Label>Coil Size</Label>
                <select
                  className="w-full px-3 py-2 border rounded"
                  value={coilinfo?.decimal || ''}
                  onChange={(e) => changecoilinfo(e.target.value)}
                >
                  <option value="">Select</option>
                  {(binding.type === 'Wire-O' ? wireo : plastikol)?.map((item) => (
                    <option key={item.decimal} value={item.decimal}>
                      {item.fraction}"
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <Label>Pitch</Label>
                <Input
                  value={coilinfo?.pitch || ''}
                  onChange={(e) => setCoilinfo((p) => ({ ...p, pitch: e.target.value }))}
                />
                :1
              </div>
              <div>
                <Label>Holes</Label>
                <Input
                  value={bookinfo?.holes || ''}
                  onChange={(e) => setBookinfo((p) => ({ ...p, holes: e.target.value }))}
                />
              </div>
              <div>
                <Label>Color</Label>
                <select className="w-full px-3 py-2 border rounded">
                  <option>White</option>
                  <option>Black</option>
                  <option>Blue</option>
                  <option>Green</option>
                  <option>Red</option>
                </select>
              </div>
            </div>
          )}
        </div>

        {/* === MISC OPTIONS === */}
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4 mb-8">
          <div><Label>Tabs</Label><Input value={bookinfo?.tabs || ''} onChange={e => setBookinfo(p => ({...p, tabs: e.target.value}))} /></div>
          <div><Label>Collate P/U</Label><Input value={bookinfo?.collatepu || ''} onChange={e => setBookinfo(p => ({...p, collatepu: e.target.value}))} /></div>
          <div><Label>Insert</Label><Input value={bookinfo?.insert || ''} onChange={e => setBookinfo(p => ({...p, insert: e.target.value}))} /></div>
          <div><Label>Cuts</Label><Input value={bookinfo?.cuts || ''} onChange={e => setBookinfo(p => ({...p, cuts: e.target.value}))} /></div>
          <div><Label>Punch M/R $</Label><Input value={bookinfo?.punchmr || ''} onChange={e => setBookinfo(p => ({...p, punchmr: e.target.value}))} /></div>
          <div><Label>Coil M/R $</Label><Input value={bookinfo?.coilmr || ''} onChange={e => setBookinfo(p => ({...p, coilmr: e.target.value}))} /></div>
          <div className="flex items-center gap-2"><Label>Slip Sheet</Label><input type="checkbox" checked={!!bookinfo?.slipsheet} onChange={e => setBookinfo(p => ({...p, slipsheet: e.target.checked}))} /></div>
        </div>



        {/* === QUANTITY TABS === */}
        {items.length > 0 && (
          <Collapse title={<strong className="text-xl">Quantities ({items.length})</strong>}>
            <Tabs>
              <TabList className="flex flex-wrap gap-3 mb-4">
                {items.map((item, i) => (
                  <Tab
                    key={item.id}
                    className="px-6 py-3 bg-blue-100 rounded-lg cursor-pointer relative"
                    selectedClassName="bg-blue-600 text-white"
                  >
                    {item.quantity.toLocaleString()}
                    {items.length > 1 && (
                      <FiMinusCircle
                        className="absolute -top-2 -right-2 text-red-600 text-xl cursor-pointer"
                        onClick={(e) => {
                          e.stopPropagation();
                          removequantity(item.id);
                        }}
                      />
                    )}
                  </Tab>
                ))}
              </TabList>
              {items.map((item) => (
                <TabPanel key={item.id}>
                  <Bookitem item={item} bookinfo={bookinfo} />
                </TabPanel>
              ))}
            </Tabs>
          </Collapse>
        )}
        {/* === QUANTITIES & PRICING — RESPONSIVE FLOATING CARDS === */}
        {items.length > 0 && (
          <div className="mt-10">
            <h2 className=" font-bold text-teal-600 mb-10 text-center">
              Quantities & Pricing
            </h2>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-5 gap-8 max-w-7xl mx-auto">
              {items.map((item) => {
                const labor = Number(getlabor(item) || 0);
                const material = Number(getmat(item) || 0);
                const total = labor + material;
                const perUnit = item.quantity > 0 ? total / item.quantity : 0;

                return (
                  <div
                    key={item.id}
                    className="relative bg-white rounded-3xl shadow-2xl border-4 border-teal-500 hover:border-teal-600 transform hover:scale-105 transition-all duration-300"
                  >
                    {/* Red Remove Button */}
                    {items.length > 1 && (
                      <button
                        onClick={() => removequantity(item.id)}
                        className="absolute -top-4 -right-4 bg-red-500 hover:bg-red-600 text-white text-3xl font-bold rounded-full w-14 h-14 flex items-center justify-center shadow-xl z-10 hover:shadow-2xl transition-all"
                      >
                        ×
                      </button>
                    )}

                    <div className="p-8 text-center">
                      {/* Quantity */}
                      <div className=" font-black text-gray-900 mb-2" style={{fontSize: '2.4rem'}}>
                        {parseInt(item.quantity).toLocaleString()}
                      </div>

     {/* Per Unit Price */}
                      <div className="text-4xl font-bold text-blue-600 mb-8">
                        ${perUnit.toFixed(4)}/unit
                      </div>

                      
                      {/* Book Summary */}
                      <div className="text-gray-600 text-sm mb-6 leading-tight">
                        {binding.type || "Wire-O"} • {bookinfo.totalpages || 0} Pages
                        <br />
                        {coilinfo.fraction ? `${coilinfo.fraction}" Coil` : "—"}
                      </div>

                 

                      {/* Cost Breakdown */}
                      <div className="text-left space-y-2 text-sm font-medium">
                        <div className="flex justify-between">
                          <span className="text-gray-600">Labor:</span>
                          <span>${labor.toFixed(2)}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-600">Material:</span>
                          <span>${material.toFixed(2)}</span>
                        </div>
                        {bookinfo.punchmr > 0 && (
                          <div className="flex justify-between">
                            <span className="text-gray-600">Punch M/R:</span>
                            <span>${Number(bookinfo.punchmr).toFixed(2)}</span>
                          </div>
                        )}
                        {bookinfo.coilmr > 0 && (
                          <div className="flex justify-between">
                            <span className="text-gray-600">Coil M/R:</span>
                            <span>${Number(bookinfo.coilmr).toFixed(2)}</span>
                          </div>
                        )}
                      </div>

                      {/* Dotted Separator */}
                      <div className="my-6 border-t-2 border-dashed border-teal-300"></div>

                      {/* Total */}
                      <div className="text-5xl font-black text-teal-600">
                        ${total.toFixed(2)}
                      </div>
                      <div className="text-teal-600 font-bold text-lg -mt-2">Total</div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* === SAVE / CANCEL BUTTONS === */}
        <div className="mt-16 flex justify-center gap-10">
          <Button
            onClick={handleSaveBook}
           style={{ padding: '14px 36px', fontSize: '1.2rem', backgroundColor: '#4EC0AC', color: 'white', fontWeight: 'bold' }}
          >
            {editingItem ? "Update Book" : "Add Book to Estimate"}
          </Button>

          <Button
            onClick={closeBook}
           
          >
            Cancel
          </Button>
        </div>
      </div>
    </div>
  );

}
export default Books